import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';

// ===== CONSTANTS (from Swing Lab) =====
const GRAVITY = -14;
const BALL_RADIUS = 0.04;
const HOLE_RADIUS = 0.12;
const YARD_SCALE = 3.28;
const FRICTION_FAIRWAY = 0.974;
const FRICTION_ROUGH = 0.955;
const FRICTION_BUNKER = 0.92;
const FRICTION_GREEN = 0.982;
const BOUNCE_FAIRWAY = 0.28;
const BOUNCE_ROUGH = 0.15;
const BOUNCE_GREEN = 0.2;

// ===== 14 CLUBS =====
const CLUBS = [
  { id: 'DR',  name: 'Driver',         abbr: 'Driver', maxDist: 293, loft: 0.18, power: 1.0,  spin: 0.3,  accuracy: 0.97 },
  { id: '3W',  name: '3 Wood',         abbr: '3W',    maxDist: 263, loft: 0.22, power: 0.92, spin: 0.35, accuracy: 0.96 },
  { id: '5H',  name: '5 Hybrid',       abbr: '5H',    maxDist: 236, loft: 0.28, power: 0.85, spin: 0.4,  accuracy: 0.95 },
  { id: '4I',  name: '4 Iron',         abbr: '4i',    maxDist: 222, loft: 0.32, power: 0.80, spin: 0.45, accuracy: 0.94 },
  { id: '5I',  name: '5 Iron',         abbr: '5i',    maxDist: 205, loft: 0.36, power: 0.76, spin: 0.5,  accuracy: 0.94 },
  { id: '6I',  name: '6 Iron',         abbr: '6i',    maxDist: 193, loft: 0.40, power: 0.72, spin: 0.55, accuracy: 0.95 },
  { id: '7I',  name: '7 Iron',         abbr: '7i',    maxDist: 185, loft: 0.44, power: 0.68, spin: 0.6,  accuracy: 0.96 },
  { id: '8I',  name: '8 Iron',         abbr: '8i',    maxDist: 170, loft: 0.48, power: 0.63, spin: 0.65, accuracy: 0.96 },
  { id: '9I',  name: '9 Iron',         abbr: '9i',    maxDist: 163, loft: 0.52, power: 0.59, spin: 0.7,  accuracy: 0.97 },
  { id: 'PW',  name: 'Pitching Wedge', abbr: 'P',     maxDist: 155, loft: 0.56, power: 0.55, spin: 0.75, accuracy: 0.97 },
  { id: '52',  name: '52 Degree',      abbr: '52°',   maxDist: 133, loft: 0.62, power: 0.48, spin: 0.82, accuracy: 0.97 },
  { id: '56',  name: '56 Degree',      abbr: '56°',   maxDist: 120, loft: 0.68, power: 0.43, spin: 0.88, accuracy: 0.98 },
  { id: '60',  name: '60 Degree',      abbr: '60°',   maxDist: 112, loft: 0.74, power: 0.38, spin: 0.92, accuracy: 0.98 },
  { id: 'PT',  name: 'Putter',         abbr: 'PT',    maxDist: 30,  loft: 0.04, power: 0.12, spin: 0.0,  accuracy: 1.0  },
];

// ===== HOLES — AMEN CORNER AT AUGUSTA =====
const HOLES = [
  {
    name: 'White Dogwood', number: 11, par: 4, tee: [0, 0, 0], pin: [-19, 0, -157],
    desc: '520 yards • Long par 4 • Approach over water to a guarded green',
    bunkers: [{ pos: [-25, 0, -148], size: [6, 5] }, { pos: [-12, 0, -153], size: [5, 4] }],
    water: [{ pos: [-28, 0, -140], size: [12, 18] }],
    trees: [
      { pos: [-25, 0, -47], s: 1.5 }, { pos: [22, 0, -63], s: 1.3 }, { pos: [19, 0, -110], s: 1.4 },
      { pos: [-13, 0, -94], s: 1.1 }, { pos: [28, 0, -141], s: 1.2 }, { pos: [-22, 0, -78], s: 1.6 },
      { pos: [15, 0, -30], s: 1.2 }, { pos: [-18, 0, -120], s: 1.3 }, { pos: [25, 0, -85], s: 1.1 },
      { pos: [-30, 0, -55], s: 1.4 },
    ],
  },
  {
    name: 'Golden Bell', number: 12, par: 3, tee: [0, 0, 0], pin: [3, 0, -47],
    desc: "155 yards • Short but deadly • Wind swirl + Rae's Creek",
    bunkers: [
      { pos: [8, 0, -50], size: [5, 3.5] }, { pos: [-5, 0, -50], size: [5, 3.5] },
      { pos: [0, 0, -54], size: [4, 2.5] },
    ],
    water: [{ pos: [0, 0, -30], size: [24, 8] }],
    trees: [
      { pos: [-15, 0, -17], s: 1.3 }, { pos: [15, 0, -22], s: 1.4 }, { pos: [-13, 0, -56], s: 1.2 },
      { pos: [13, 0, -58], s: 1.3 }, { pos: [-20, 0, -35], s: 1.1 }, { pos: [20, 0, -40], s: 1.2 },
    ],
  },
  {
    name: 'Azalea', number: 13, par: 5, tee: [0, 0, 0], pin: [-24, 0, -164],
    desc: "545 yards • Risk/reward par 5 • Rae's Creek guards the green",
    bunkers: [
      { pos: [-15, 0, -75], size: [6, 4] }, { pos: [-30, 0, -155], size: [5, 4] },
      { pos: [-18, 0, -160], size: [4, 3] },
    ],
    water: [{ pos: [-9, 0, -54], size: [10, 8] }, { pos: [-24, 0, -130], size: [14, 12] }],
    trees: [
      { pos: [-18, 0, -36], s: 1.5 }, { pos: [24, 0, -45], s: 1.3 }, { pos: [18, 0, -90], s: 1.4 },
      { pos: [-30, 0, -105], s: 1.6 }, { pos: [15, 0, -144], s: 1.2 }, { pos: [-9, 0, -174], s: 1.1 },
      { pos: [10, 0, -20], s: 1.3 }, { pos: [-25, 0, -70], s: 1.4 }, { pos: [22, 0, -120], s: 1.2 },
      { pos: [-15, 0, -145], s: 1.3 },
    ],
  },
];

// ===== SOUND ENGINE =====
class SoundEngine {
  constructor() { this.ctx = null; this.enabled = true; }
  init() {
    if (this.ctx) return;
    try { this.ctx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch (e) { this.enabled = false; }
  }
  play(type) {
    if (!this.enabled || !this.ctx) return;
    try {
      const now = this.ctx.currentTime;
      const gain = this.ctx.createGain();
      gain.connect(this.ctx.destination);
      const osc = (freqStart, freqEnd, dur, g, waveform) => {
        const o = this.ctx.createOscillator();
        o.type = waveform || 'sine';
        o.frequency.setValueAtTime(freqStart, now);
        o.frequency.exponentialRampToValueAtTime(freqEnd, now + dur);
        gain.gain.setValueAtTime(g, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
        o.connect(gain);
        o.start(now);
        o.stop(now + dur + 0.02);
      };
      const noise = (dur, filterFreq, g, type) => {
        const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (data.length * 0.3));
        const n = this.ctx.createBufferSource();
        n.buffer = buf;
        const f = this.ctx.createBiquadFilter();
        f.type = type || 'lowpass';
        f.frequency.value = filterFreq;
        const ng = this.ctx.createGain();
        ng.gain.setValueAtTime(g, now);
        ng.gain.exponentialRampToValueAtTime(0.001, now + dur);
        n.connect(f); f.connect(ng); ng.connect(this.ctx.destination);
        n.start(now);
      };
      if (type === 'driver') { osc(180, 60, 0.22, 0.38, 'sawtooth'); noise(0.1, 2000, 0.22, 'lowpass'); }
      else if (type === 'iron') { osc(800, 200, 0.12, 0.28, 'triangle'); }
      else if (type === 'wedge') { osc(1200, 400, 0.1, 0.24, 'sine'); }
      else if (type === 'putter') { osc(500, 300, 0.1, 0.16, 'sine'); }
      else if (type === 'land') { osc(120, 50, 0.14, 0.14, 'sine'); }
      else if (type === 'hole') {
        [400, 500, 650, 820].forEach((f, i) => {
          const o = this.ctx.createOscillator();
          const g = this.ctx.createGain();
          o.type = 'sine';
          o.frequency.setValueAtTime(f, now + i * 0.11);
          g.gain.setValueAtTime(0.22, now + i * 0.11);
          g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.11 + 0.22);
          o.connect(g); g.connect(this.ctx.destination);
          o.start(now + i * 0.11); o.stop(now + i * 0.11 + 0.26);
        });
      }
      else if (type === 'splash') { noise(0.4, 1500, 0.3, 'lowpass'); }
      else if (type === 'sand') { noise(0.2, 3000, 0.2, 'highpass'); }
      else if (type === 'tree') { osc(200, 80, 0.1, 0.2, 'square'); }
    } catch (e) { /* silent */ }
  }
}

// ===== TEST IDs =====
const T = {
  intro: 'loop-game-intro',
  teeOff: 'loop-game-tee-off',
  exit: 'loop-game-exit',
  canvas: 'loop-game-canvas',
  swing: 'loop-game-swing-btn',
  aimLeft: 'loop-game-aim-left',
  aimRight: 'loop-game-aim-right',
  clubPrev: 'loop-game-club-prev',
  clubNext: 'loop-game-club-next',
  nextHole: 'loop-game-next-hole',
  scorecard: 'loop-game-scorecard',
  playAgain: 'loop-game-play-again',
  exitScorecard: 'loop-game-exit-scorecard',
  leaderboard: 'loop-game-leaderboard',
  leaderboardBtn: 'loop-game-leaderboard-btn',
  submitScore: 'loop-game-submit-score',
  playerNameInput: 'loop-game-player-name',
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ===== COLORS =====
const COLORS = {
  bg: '#0a1f2a',
  gold: '#d4af37',
  goldLight: '#e8cc6e',
  silver: '#c0c0c0',
  gray: '#909090',
  dark: '#1a1a1a',
  panel: 'rgba(0,0,0,0.82)',
  panelDark: 'rgba(10,31,42,0.95)',
  border: 'rgba(192,192,192,0.22)',
  borderGold: 'rgba(212,175,55,0.5)',
  white: '#e0e0e0',
  good: '#4ade80',
  bad: '#f87171',
};

// ===== BUILD 3D SCENE (raw Three.js) =====
function buildScene(scene, holeData) {
  const group = new THREE.Group();

  const pX = holeData.pin[0];
  const pZ = holeData.pin[2];
  const len = Math.abs(pZ) + 40;
  const cX = pX * 0.5;
  const cZ = pZ / 2;
  const fw = Math.min(30, Math.max(18, len * 0.15));

  // Rough (base huge plane)
  const roughGeo = new THREE.PlaneGeometry(120, len + 80);
  const roughMat = new THREE.MeshStandardMaterial({ color: 0x1a5a22, flatShading: true });
  const rough = new THREE.Mesh(roughGeo, roughMat);
  rough.rotation.x = -Math.PI / 2;
  rough.position.set(0, -0.01, cZ);
  rough.receiveShadow = true;
  group.add(rough);

  // Rough edges (darker alongside fairway)
  const edgeMat = new THREE.MeshStandardMaterial({ color: 0x1a6a28, flatShading: true });
  const leftEdge = new THREE.Mesh(new THREE.PlaneGeometry(fw * 0.7, len + 40), edgeMat);
  leftEdge.rotation.x = -Math.PI / 2;
  leftEdge.position.set(cX - fw * 0.9, 0, cZ);
  group.add(leftEdge);
  const rightEdge = new THREE.Mesh(new THREE.PlaneGeometry(fw * 0.7, len + 40), edgeMat);
  rightEdge.rotation.x = -Math.PI / 2;
  rightEdge.position.set(cX + fw * 0.9, 0, cZ);
  group.add(rightEdge);

  // Fairway
  const fwGeo = new THREE.PlaneGeometry(fw, len);
  const fwMat = new THREE.MeshStandardMaterial({ color: 0x2d8a3a, flatShading: true });
  const fairway = new THREE.Mesh(fwGeo, fwMat);
  fairway.rotation.x = -Math.PI / 2;
  fairway.position.set(cX, 0, cZ);
  fairway.receiveShadow = true;
  group.add(fairway);

  // Fairway stripes (mow pattern)
  const stripeCount = Math.floor(len / 4);
  for (let i = 0; i < stripeCount; i++) {
    const stripe = new THREE.Mesh(
      new THREE.PlaneGeometry(fw - 2, 1.8),
      new THREE.MeshStandardMaterial({ color: i % 2 === 0 ? 0x2f8c3c : 0x2a8236, transparent: true, opacity: 0.45, flatShading: true })
    );
    stripe.rotation.x = -Math.PI / 2;
    stripe.position.set(cX, 0.003, -i * 4 + 5);
    group.add(stripe);
  }

  // Tee box
  const tee = new THREE.Mesh(
    new THREE.PlaneGeometry(4, 3),
    new THREE.MeshStandardMaterial({ color: 0x3a9a4a, flatShading: true })
  );
  tee.rotation.x = -Math.PI / 2;
  tee.position.set(0, 0.012, 1);
  group.add(tee);

  // Bunkers
  holeData.bunkers.forEach((b) => {
    const bun = new THREE.Mesh(
      new THREE.CircleGeometry(1, 20),
      new THREE.MeshStandardMaterial({ color: 0xc4a84d, flatShading: true })
    );
    bun.rotation.x = -Math.PI / 2;
    bun.scale.set(b.size[0] / 2, b.size[1] / 2, 1);
    bun.position.set(b.pos[0], 0.008, b.pos[2]);
    bun.receiveShadow = true;
    group.add(bun);
  });

  // Water hazards (with ref stored for animation)
  const waterMeshes = [];
  holeData.water.forEach((w) => {
    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(w.size[0], w.size[1]),
      new THREE.MeshStandardMaterial({ color: 0x1a7090, transparent: true, opacity: 0.85, flatShading: true })
    );
    water.rotation.x = -Math.PI / 2;
    water.position.set(w.pos[0], 0.005, w.pos[2]);
    group.add(water);
    waterMeshes.push(water);
  });

  // Trees (low-poly stacked cones)
  holeData.trees.forEach((t) => {
    const s = t.s;
    const tree = new THREE.Group();
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08 * s, 0.14 * s, 1.6 * s, 6),
      new THREE.MeshStandardMaterial({ color: 0x5a3a1a, flatShading: true })
    );
    trunk.position.y = 0.8 * s;
    trunk.castShadow = true;
    tree.add(trunk);
    const c1 = new THREE.Mesh(
      new THREE.ConeGeometry(1.0 * s, 1.8 * s, 6),
      new THREE.MeshStandardMaterial({ color: 0x1a6a2a, flatShading: true })
    );
    c1.position.y = 2.0 * s; c1.castShadow = true;
    tree.add(c1);
    const c2 = new THREE.Mesh(
      new THREE.ConeGeometry(0.75 * s, 1.3 * s, 6),
      new THREE.MeshStandardMaterial({ color: 0x228a3a, flatShading: true })
    );
    c2.position.y = 2.8 * s; c2.castShadow = true;
    tree.add(c2);
    const c3 = new THREE.Mesh(
      new THREE.ConeGeometry(0.5 * s, 0.9 * s, 6),
      new THREE.MeshStandardMaterial({ color: 0x2a9a4a, flatShading: true })
    );
    c3.position.y = 3.4 * s; c3.castShadow = true;
    tree.add(c3);
    tree.position.set(t.pos[0], 0, t.pos[2]);
    group.add(tree);
  });

  // Green (at pin)
  const green = new THREE.Mesh(
    new THREE.CircleGeometry(4, 32),
    new THREE.MeshStandardMaterial({ color: 0x3ab050, flatShading: true })
  );
  green.rotation.x = -Math.PI / 2;
  green.position.set(holeData.pin[0], 0.005, holeData.pin[2]);
  group.add(green);

  // Hole (cup)
  const cup = new THREE.Mesh(
    new THREE.CircleGeometry(HOLE_RADIUS, 16),
    new THREE.MeshStandardMaterial({ color: 0x0a0a0a })
  );
  cup.rotation.x = -Math.PI / 2;
  cup.position.set(holeData.pin[0], 0.008, holeData.pin[2]);
  group.add(cup);

  // Flag pole
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.015, 0.015, 2.6, 8),
    new THREE.MeshStandardMaterial({ color: 0xe0e0e0 })
  );
  pole.position.set(holeData.pin[0], 1.3, holeData.pin[2]);
  pole.castShadow = true;
  group.add(pole);

  // Flag (with wave animation handle)
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.44, 0.28),
    new THREE.MeshStandardMaterial({ color: 0xcc0000, side: THREE.DoubleSide })
  );
  flag.position.set(holeData.pin[0] + 0.22, 2.45, holeData.pin[2]);
  group.add(flag);

  scene.add(group);
  return { group, flag, waterMeshes };
}

// ===== MAIN COMPONENT =====
const TheLoopGame = () => {
  const navigate = useNavigate();

  // High-level view: title -> playing -> scorecard -> leaderboard
  const [gameView, setGameView] = useState('title');
  // Within playing: aiming -> power -> moving -> holed
  const [gameState, setGameState] = useState('aiming');
  const [currentHole, setCurrentHole] = useState(0);
  const [strokes, setStrokes] = useState(0);
  const [scores, setScores] = useState([]); // per-hole stroke counts
  const [power, setPower] = useState(0);
  const [powerDir, setPowerDir] = useState(1);
  const [message, setMessage] = useState('');
  const [wind, setWind] = useState({ speed: 0, dir: 0 });
  const [clubIdx, setClubIdx] = useState(0);
  const [distToHole, setDistToHole] = useState(0);
  const [lie, setLie] = useState('Tee');

  // Leaderboard state
  const [playerName, setPlayerName] = useState('');
  const [submittingScore, setSubmittingScore] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [savedScoreId, setSavedScoreId] = useState(null);
  const [submitError, setSubmitError] = useState('');

  // Blink effect for title screen "TEE OFF" prompt
  const [blinking, setBlinking] = useState(true);
  useEffect(() => {
    if (gameView !== 'title') return;
    const iv = setInterval(() => setBlinking((b) => !b), 600);
    return () => clearInterval(iv);
  }, [gameView]);

  // Refs for animation loop (don't want stale closures)
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const ballMeshRef = useRef(null);
  const trailMeshRef = useRef(null);
  const trailGeoRef = useRef(null);
  const aimArrowRef = useRef(null);
  const flagRef = useRef(null);
  const waterMeshesRef = useRef([]);
  const courseGroupRef = useRef(null);
  const rafRef = useRef(null);
  const soundRef = useRef(null);

  const ballPosRef = useRef([0, BALL_RADIUS, 0]);
  const ballVelRef = useRef([0, 0, 0]);
  const aimAngleRef = useRef(0);
  const aimVisRef = useRef(true);
  const trailRef = useRef([]);
  const gameStateRef = useRef('aiming');
  const strokesRef = useRef(0);
  const clubIdxRef = useRef(0);
  const currentHoleRef = useRef(0);
  const windRef = useRef({ speed: 0, dir: 0 });

  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  useEffect(() => { strokesRef.current = strokes; }, [strokes]);
  useEffect(() => { clubIdxRef.current = clubIdx; }, [clubIdx]);
  useEffect(() => { currentHoleRef.current = currentHole; }, [currentHole]);
  useEffect(() => { windRef.current = wind; }, [wind]);

  const calcAim = (from, to) => Math.atan2(to[0] - from[0], -(to[2] - from[2]));

  const getLie = (pos, holeData) => {
    const [x, , z] = pos;
    for (const b of holeData.bunkers) {
      const dx = x - b.pos[0];
      const dz = z - b.pos[2];
      if (Math.abs(dx) < b.size[0] / 2 && Math.abs(dz) < b.size[1] / 2) return 'Bunker';
    }
    const pX = holeData.pin[0];
    const pZ = holeData.pin[2];
    if (Math.sqrt((x - pX) ** 2 + (z - pZ) ** 2) < 4) return 'Green';
    const cX = pX * 0.5;
    const len = Math.abs(pZ);
    const fw = Math.min(30, Math.max(18, len * 0.15));
    if (Math.abs(x - cX) < fw / 2) return 'Fairway';
    return 'Rough';
  };

  const suggestClub = (dist, lieNow) => {
    if (lieNow === 'Green' || dist < 15) return CLUBS.length - 1;
    if (lieNow === 'Bunker') return CLUBS.findIndex((c) => c.id === '56');
    for (let i = CLUBS.length - 2; i >= 0; i--) {
      if (CLUBS[i].maxDist >= dist) return i;
    }
    return 0;
  };

  const updateInfo = useCallback((pos, holeData) => {
    const dx = pos[0] - holeData.pin[0];
    const dz = pos[2] - holeData.pin[2];
    const d = Math.round(Math.sqrt(dx * dx + dz * dz) * YARD_SCALE);
    setDistToHole(d);
    const l = getLie(pos, holeData);
    setLie(l);
    setClubIdx(suggestClub(d, l));
  }, []);

  // ===== LOAD/UNLOAD COURSE GROUP =====
  const loadHoleIntoScene = useCallback((idx) => {
    const scene = sceneRef.current;
    if (!scene) return;
    // Remove previous course group
    if (courseGroupRef.current) {
      scene.remove(courseGroupRef.current);
      courseGroupRef.current.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      courseGroupRef.current = null;
    }
    const { group, flag, waterMeshes } = buildScene(scene, HOLES[idx]);
    courseGroupRef.current = group;
    flagRef.current = flag;
    waterMeshesRef.current = waterMeshes;
  }, []);

  // ===== INIT HOLE STATE =====
  const initHole = useCallback((idx) => {
    const h = HOLES[idx];
    ballPosRef.current = [h.tee[0], BALL_RADIUS, h.tee[2]];
    ballVelRef.current = [0, 0, 0];
    aimAngleRef.current = calcAim(h.tee, h.pin);
    aimVisRef.current = true;
    trailRef.current = [];
    setStrokes(0);
    setPower(0);
    setPowerDir(1);
    setMessage('');
    setGameState('aiming');
    setWind({ speed: Math.round(Math.random() * 10 + 1), dir: Math.random() * Math.PI * 2 });
    updateInfo(h.tee, h);
    setLie('Tee');
    loadHoleIntoScene(idx);
  }, [updateInfo, loadHoleIntoScene]);

  // ===== START GAME =====
  const startGame = () => {
    if (!soundRef.current) soundRef.current = new SoundEngine();
    soundRef.current.init();
    setCurrentHole(0);
    setScores([]);
    setSavedScoreId(null);
    setPlayerName('');
    setSubmitError('');
    setGameView('playing');
    // initHole will run when 3D is mounted below via effect
  };

  // ===== LEADERBOARD HELPERS =====
  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/game/leaderboard?limit=25`);
      const data = await res.json();
      setLeaderboard(Array.isArray(data.scores) ? data.scores : []);
    } catch (err) {
      console.error('Leaderboard fetch error:', err);
    }
  }, []);

  const submitScore = useCallback(async () => {
    const name = playerName.trim();
    if (!name) return;
    setSubmittingScore(true);
    setSubmitError('');
    try {
      const total = scores.reduce((a, b) => a + b, 0);
      const par = HOLES.reduce((s, h) => s + h.par, 0);
      const res = await fetch(`${API_BASE}/api/game/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerName: name,
          totalStrokes: total,
          totalPar: par,
          holeScores: HOLES.map((h, i) => ({
            hole: h.number,
            name: h.name,
            par: h.par,
            strokes: scores[i] ?? 0,
          })),
        }),
      });
      const data = await res.json();
      if (data && data.success) {
        setSavedScoreId(data.score?.id ?? null);
        setLeaderboard(Array.isArray(data.leaderboard) ? data.leaderboard : []);
        setGameView('leaderboard');
      } else {
        setSubmitError('Could not save score. Please try again.');
      }
    } catch (err) {
      console.error('Score submit error:', err);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmittingScore(false);
    }
  }, [playerName, scores]);

  // Preload leaderboard on title screen
  useEffect(() => {
    if (gameView === 'title') {
      fetchLeaderboard();
    }
  }, [gameView, fetchLeaderboard]);

  // ===== THREE.JS LIFECYCLE =====
  useEffect(() => {
    if (gameView !== 'playing' || !mountRef.current) return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb);
    scene.fog = new THREE.Fog(0xa8c8b8, 80, 250);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 500);
    camera.position.set(0, 3, 5);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mountRef.current.appendChild(renderer.domElement);
    renderer.domElement.setAttribute('data-testid', T.canvas);
    renderer.domElement.style.touchAction = 'none';
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    rendererRef.current = renderer;

    // Lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(ambient);
    const sun = new THREE.DirectionalLight(0xfff8e0, 1.25);
    sun.position.set(12, 18, 8);
    sun.castShadow = true;
    sun.shadow.mapSize.width = 2048;
    sun.shadow.mapSize.height = 2048;
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 300;
    sun.shadow.camera.left = -80;
    sun.shadow.camera.right = 80;
    sun.shadow.camera.top = 80;
    sun.shadow.camera.bottom = -80;
    scene.add(sun);
    const fill = new THREE.DirectionalLight(0x87ceeb, 0.28);
    fill.position.set(-6, 10, -4);
    scene.add(fill);
    const hemi = new THREE.HemisphereLight(0x87ceeb, 0x2c6e49, 0.45);
    scene.add(hemi);

    // Ball
    const ballGeo = new THREE.SphereGeometry(BALL_RADIUS, 16, 16);
    const ballMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x888888, emissiveIntensity: 0.15 });
    const ball = new THREE.Mesh(ballGeo, ballMat);
    ball.castShadow = true;
    scene.add(ball);
    ballMeshRef.current = ball;

    // Aim arrow group
    const aimGroup = new THREE.Group();
    const arrowShaft = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.015, 5),
      new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 })
    );
    arrowShaft.position.set(0, 0, -2.5);
    aimGroup.add(arrowShaft);
    const arrowHead = new THREE.Mesh(
      new THREE.ConeGeometry(0.14, 0.4, 4),
      new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.6 })
    );
    arrowHead.rotation.x = Math.PI / 2;
    arrowHead.position.set(0, 0, -5.2);
    aimGroup.add(arrowHead);
    for (let i = 1; i <= 6; i++) {
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.025, 6, 6),
        new THREE.MeshStandardMaterial({ color: 0xd4af37, emissive: 0xd4af37, emissiveIntensity: 0.35 })
      );
      dot.position.set(0, 0.02, -i * 0.8);
      aimGroup.add(dot);
    }
    scene.add(aimGroup);
    aimArrowRef.current = aimGroup;

    // Trail (points buffer)
    const trailGeo = new THREE.BufferGeometry();
    const trailPositions = new Float32Array(300 * 3);
    trailGeo.setAttribute('position', new THREE.BufferAttribute(trailPositions, 3));
    trailGeo.setDrawRange(0, 0);
    const trailMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.08, transparent: true, opacity: 0.45, sizeAttenuation: true });
    const trailPts = new THREE.Points(trailGeo, trailMat);
    scene.add(trailPts);
    trailMeshRef.current = trailPts;
    trailGeoRef.current = trailGeo;

    // Build first hole
    loadHoleIntoScene(currentHoleRef.current);
    initHole(currentHoleRef.current);

    // Animation loop
    const clock = new THREE.Clock();
    const tPos = new THREE.Vector3(0, 3, 5);
    const tLook = new THREE.Vector3(0, 0, -5);

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      // Flag wave
      if (flagRef.current) flagRef.current.rotation.y = Math.sin(t * 3) * 0.1;
      // Water shimmer
      waterMeshesRef.current.forEach((w) => {
        if (w.material) w.material.opacity = 0.78 + Math.sin(t * 2) * 0.06;
      });

      // Ball position
      const [bx, by, bz] = ballPosRef.current;
      if (ballMeshRef.current) ballMeshRef.current.position.set(bx, by, bz);

      // Aim arrow
      if (aimArrowRef.current) {
        aimArrowRef.current.position.set(bx, 0.05, bz);
        aimArrowRef.current.rotation.y = aimAngleRef.current;
        aimArrowRef.current.visible = aimVisRef.current;
      }

      // Trail buffer
      if (trailGeoRef.current && trailRef.current.length > 0) {
        const arr = trailGeoRef.current.attributes.position.array;
        const pts = trailRef.current;
        for (let i = 0; i < pts.length; i++) {
          arr[i * 3] = pts[i][0];
          arr[i * 3 + 1] = pts[i][1];
          arr[i * 3 + 2] = pts[i][2];
        }
        trailGeoRef.current.setDrawRange(0, pts.length);
        trailGeoRef.current.attributes.position.needsUpdate = true;
      } else if (trailGeoRef.current) {
        trailGeoRef.current.setDrawRange(0, 0);
      }

      // Camera follow
      const a = aimAngleRef.current;
      let cx, cy, cz, lx, ly, lz;
      if (gameStateRef.current === 'moving') {
        cx = bx + Math.sin(a) * 3;
        cy = Math.max(by + 2.5, 3.5);
        cz = bz + Math.cos(a) * 3;
        lx = bx - Math.sin(a) * 5;
        ly = Math.max(by * 0.5, 0.3);
        lz = bz - Math.cos(a) * 5;
      } else {
        cx = bx + Math.sin(a) * 4.5;
        cy = 2.2;
        cz = bz + Math.cos(a) * 4.5;
        lx = bx - Math.sin(a) * 10;
        ly = 0.2;
        lz = bz - Math.cos(a) * 10;
      }
      tPos.lerp(new THREE.Vector3(cx, cy, cz), 0.08);
      tLook.lerp(new THREE.Vector3(lx, ly, lz), 0.08);
      camera.position.copy(tPos);
      camera.lookAt(tLook);

      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    const mountEl = mountRef.current;
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (courseGroupRef.current) {
        scene.remove(courseGroupRef.current);
        courseGroupRef.current.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
            else obj.material.dispose();
          }
        });
      }
      if (ballMeshRef.current) {
        ballMeshRef.current.geometry.dispose();
        ballMeshRef.current.material.dispose();
      }
      if (trailGeoRef.current) trailGeoRef.current.dispose();
      if (trailMeshRef.current) trailMeshRef.current.material.dispose();
      if (aimArrowRef.current) {
        aimArrowRef.current.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) obj.material.dispose();
        });
      }
      if (renderer.domElement && mountEl && mountEl.contains(renderer.domElement)) {
        mountEl.removeChild(renderer.domElement);
      }
      renderer.dispose();
      sceneRef.current = null;
      courseGroupRef.current = null;
      ballMeshRef.current = null;
      trailGeoRef.current = null;
      trailMeshRef.current = null;
      aimArrowRef.current = null;
      flagRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameView]);

  // ===== LAUNCH BALL =====
  const launchBall = useCallback(() => {
    const club = CLUBS[clubIdxRef.current];
    const powerFrac = power / 100;
    const angle = aimAngleRef.current;
    const loft = club.loft;

    const targetUnits = club.maxDist / YARD_SCALE;
    const flightTarget = targetUnits * 0.6;
    const sin2loft = Math.sin(2 * loft);
    let v0;
    if (club.id === 'PT') {
      v0 = targetUnits * 1.8;
    } else if (sin2loft > 0.01) {
      v0 = Math.sqrt(flightTarget * Math.abs(GRAVITY) / sin2loft);
    } else {
      v0 = Math.sqrt(flightTarget * Math.abs(GRAVITY) / 0.01);
    }
    const speed = powerFrac * v0;
    const wobble = (1 - club.accuracy) * (Math.random() - 0.5) * 0.15;
    const finalAngle = angle + wobble;
    const vx = -Math.sin(finalAngle) * speed * Math.cos(loft);
    const vy = club.id === 'PT' ? 0.15 : speed * Math.sin(loft);
    const vz = Math.cos(finalAngle) * speed * Math.cos(loft);
    const w = windRef.current;
    const we = w.speed * 0.008;
    ballVelRef.current = [vx + we * Math.cos(w.dir), vy, -vz + we * Math.sin(w.dir)];

    trailRef.current = [];
    aimVisRef.current = false;
    setStrokes((s) => s + 1);
    setGameState('moving');

    const sound = soundRef.current;
    if (sound) {
      if (club.id === 'PT') sound.play('putter');
      else if (club.id === 'DR' || club.id === '3W') sound.play('driver');
      else if (club.loft > 0.55) sound.play('wedge');
      else sound.play('iron');
    }
  }, [power]);

  // ===== POWER OSCILLATION =====
  useEffect(() => {
    if (gameState !== 'power') return;
    const iv = setInterval(() => {
      setPower((p) => {
        let n = p + powerDir * 1.8;
        if (n >= 100) { setPowerDir(-1); return 100; }
        if (n <= 0) { setPowerDir(1); return 0; }
        return n;
      });
    }, 16);
    return () => clearInterval(iv);
  }, [gameState, powerDir]);

  // ===== PHYSICS LOOP =====
  useEffect(() => {
    if (gameView !== 'playing' || gameState !== 'moving') return;
    const hole = HOLES[currentHoleRef.current];
    let fc = 0;
    let landed = false;
    let rafId;

    const step = () => {
      const dt = 0.016;
      let [x, y, z] = ballPosRef.current;
      let [vx, vy, vz] = ballVelRef.current;

      vy += GRAVITY * dt;
      x += vx * dt;
      y += vy * dt;
      z += vz * dt;

      if (y <= BALL_RADIUS) {
        y = BALL_RADIUS;
        const lieNow = getLie([x, y, z], hole);
        if (!landed) {
          landed = true;
          if (lieNow === 'Bunker') soundRef.current?.play('sand');
          else soundRef.current?.play('land');
        }
        if (vy < -0.3) {
          const bounce =
            lieNow === 'Rough' ? BOUNCE_ROUGH :
            lieNow === 'Green' ? BOUNCE_GREEN :
            BOUNCE_FAIRWAY;
          vy = -vy * bounce;
          const club = CLUBS[clubIdxRef.current];
          const spinFactor = club.spin * 0.15;
          vx *= (1 - spinFactor);
          vz *= (1 - spinFactor);
        } else {
          vy = 0;
          const fric =
            lieNow === 'Bunker' ? FRICTION_BUNKER :
            lieNow === 'Rough' ? FRICTION_ROUGH :
            lieNow === 'Green' ? FRICTION_GREEN :
            FRICTION_FAIRWAY;
          vx *= fric;
          vz *= fric;
        }
      }

      // Water penalty
      for (const w of hole.water) {
        if (Math.abs(x - w.pos[0]) < w.size[0] / 2 && Math.abs(z - w.pos[2]) < w.size[1] / 2 && y <= BALL_RADIUS + 0.15) {
          const spd = Math.sqrt(vx * vx + vz * vz);
          if (spd < 2) {
            soundRef.current?.play('splash');
            setStrokes((s) => s + 1);
            setMessage('WATER! +1 STROKE');
            setTimeout(() => setMessage(''), 2200);
            ballPosRef.current = [hole.tee[0], BALL_RADIUS, hole.tee[2]];
            ballVelRef.current = [0, 0, 0];
            aimAngleRef.current = calcAim(hole.tee, hole.pin);
            aimVisRef.current = true;
            trailRef.current = [];
            updateInfo(hole.tee, hole);
            setLie('Tee');
            setGameState('aiming');
            return;
          }
        }
      }

      // Tree collision
      for (const t of hole.trees) {
        const dx = x - t.pos[0];
        const dz = z - t.pos[2];
        const distT = Math.sqrt(dx * dx + dz * dz);
        const tr = 0.6 * t.s;
        if (distT < tr && y < 2.5 * t.s) {
          soundRef.current?.play('tree');
          const nx = dx / distT;
          const nz = dz / distT;
          vx = nx * Math.abs(vx) * 0.35;
          vz = nz * Math.abs(vz) * 0.35;
          vy *= 0.3;
          x = t.pos[0] + nx * (tr + 0.15);
          z = t.pos[2] + nz * (tr + 0.15);
        }
      }

      // Hole (cup) detection
      const hx = hole.pin[0], hz = hole.pin[2];
      const dh = Math.sqrt((x - hx) ** 2 + (z - hz) ** 2);
      const spd = Math.sqrt(vx * vx + vz * vz);
      if (dh < HOLE_RADIUS && y <= BALL_RADIUS + 0.05 && spd < 2.5) {
        soundRef.current?.play('hole');
        ballPosRef.current = [hx, BALL_RADIUS * 0.5, hz];
        ballVelRef.current = [0, 0, 0];
        const s = strokesRef.current;
        const par = hole.par;
        let msg;
        if (s === 1) msg = 'HOLE IN ONE!';
        else if (s <= par - 2) msg = 'EAGLE!';
        else if (s === par - 1) msg = 'BIRDIE!';
        else if (s === par) msg = 'PAR';
        else if (s === par + 1) msg = 'BOGEY';
        else if (s === par + 2) msg = 'DOUBLE BOGEY';
        else msg = `+${s - par}`;
        setMessage(msg);
        setGameState('holed');
        return;
      }

      // Trail
      fc++;
      if (fc % 2 === 0 && (Math.abs(vx) > 0.05 || Math.abs(vy) > 0.05 || Math.abs(vz) > 0.05)) {
        trailRef.current = [...trailRef.current.slice(-80), [x, y, z]];
      }

      ballPosRef.current = [x, y, z];
      ballVelRef.current = [vx, vy, vz];

      // Stopped check
      if (y <= BALL_RADIUS + 0.01 && spd < 0.03 && Math.abs(vy) < 0.03) {
        ballVelRef.current = [0, 0, 0];
        aimAngleRef.current = calcAim([x, y, z], hole.pin);
        aimVisRef.current = true;
        trailRef.current = [];
        updateInfo([x, y, z], hole);
        setGameState('aiming');
        return;
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => { if (rafId) cancelAnimationFrame(rafId); };
  }, [gameState, gameView, updateInfo]);

  // ===== NEXT HOLE =====
  const nextHole = () => {
    const ns = [...scores, strokesRef.current];
    setScores(ns);
    if (currentHoleRef.current >= HOLES.length - 1) {
      setGameView('scorecard');
    } else {
      const n = currentHoleRef.current + 1;
      setCurrentHole(n);
      initHole(n);
    }
  };

  // ===== KEYBOARD CONTROLS =====
  useEffect(() => {
    if (gameView !== 'playing') return;
    const onKeyDown = (e) => {
      if (gameStateRef.current === 'aiming') {
        if (e.key === 'ArrowLeft' || e.key === 'a') { aimAngleRef.current -= 0.03; }
        else if (e.key === 'ArrowRight' || e.key === 'd') { aimAngleRef.current += 0.03; }
        else if (e.key === 'ArrowUp' || e.key === 'q') { e.preventDefault(); setClubIdx((i) => Math.max(0, i - 1)); }
        else if (e.key === 'ArrowDown' || e.key === 'e') { e.preventDefault(); setClubIdx((i) => Math.min(CLUBS.length - 1, i + 1)); }
        else if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
          e.preventDefault();
          setPower(0); setPowerDir(1); setGameState('power');
        }
      } else if (gameStateRef.current === 'power') {
        if (e.key === 'Escape') { e.preventDefault(); setGameState('aiming'); setPower(0); }
      }
    };
    const onKeyUp = (e) => {
      if (gameStateRef.current === 'power' && (e.key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        launchBall();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [gameView, launchBall]);

  // ===== DRAG-TO-AIM (pointer on the canvas area) =====
  const dragRef = useRef({ active: false, lastX: 0 });
  const handlePointerDown = (e) => {
    if (gameStateRef.current !== 'aiming') return;
    dragRef.current.active = true;
    dragRef.current.lastX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
  };
  const handlePointerMove = (e) => {
    if (!dragRef.current.active || gameStateRef.current !== 'aiming') return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const delta = x - dragRef.current.lastX;
    aimAngleRef.current += delta * 0.003;
    dragRef.current.lastX = x;
  };
  const handlePointerUp = () => {
    dragRef.current.active = false;
  };

  // ===== SWING BUTTON =====
  const handleSwingDown = (e) => {
    if (e) e.stopPropagation();
    if (gameStateRef.current !== 'aiming') return;
    soundRef.current?.init();
    setPower(0); setPowerDir(1); setGameState('power');
  };
  const handleSwingUp = (e) => {
    if (e) e.stopPropagation();
    if (gameStateRef.current === 'power') launchBall();
  };

  // ===== AIM NUDGE BUTTONS (fallback on small screens) =====
  const nudgeAim = (dir) => {
    if (gameStateRef.current !== 'aiming') return;
    aimAngleRef.current += dir * 0.05;
  };

  // ===== HELPERS =====
  const getScoreName = (s, par) => {
    if (s === 1) return 'ACE';
    const d = s - par;
    if (d <= -2) return 'EAGLE';
    if (d === -1) return 'BIRDIE';
    if (d === 0) return 'PAR';
    if (d === 1) return 'BOGEY';
    if (d === 2) return 'DBL BOG';
    return `+${d}`;
  };
  const windDirLabel = () => {
    const d = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return d[Math.abs(Math.round(wind.dir / (Math.PI / 4))) % 8];
  };
  const totalPar = HOLES.reduce((s, h) => s + h.par, 0);
  const totalStrokes = scores.reduce((a, b) => a + b, 0) + (gameView === 'scorecard' ? 0 : strokes);
  const club = CLUBS[clubIdx];

  // ===================================================================
  // TITLE SCREEN
  // ===================================================================
  if (gameView === 'title') {
    return (
      <div
        data-testid={T.intro}
        className="crt"
        style={{
          position: 'fixed', inset: 0, background: '#0a0a0a', color: '#00ff00',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '20px', overflow: 'auto', userSelect: 'none',
          fontFamily: "'IBM Plex Mono', 'Press Start 2P', monospace",
        }}
      >
        <div style={{ textAlign: 'center', width: '100%', maxWidth: '600px' }}>
          <div style={{
            marginBottom: '24px', display: 'flex', justifyContent: 'center',
            alignItems: 'center', userSelect: 'none',
          }}>
            <img
              src="/logo.png"
              alt="Blue Heron"
              style={{
                width: '110px', height: '110px', imageRendering: 'pixelated',
                display: 'block', pointerEvents: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '14px' }}>
            <span style={{
              color: '#FFC857', fontSize: '14px', letterSpacing: '0.2em',
            }}>
              ★ WELCOME TO ★
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(36px, 9vw, 56px)', color: '#4AE54A', margin: 0,
            fontFamily: "'Press Start 2P', monospace", lineHeight: 1.4,
            letterSpacing: '0.02em',
          }}>
            THE LOOP
          </h1>

          <p style={{
            color: '#FEFEE3', fontSize: '13px', marginTop: '14px', marginBottom: '6px',
            letterSpacing: '0.15em',
          }}>
            AMEN CORNER
          </p>
          <p style={{
            color: '#FFC857', fontSize: '11px', margin: 0,
            letterSpacing: '0.18em',
          }}>
            AUGUSTA INSPIRED
          </p>

          <div style={{
            display: 'flex', justifyContent: 'center', gap: '24px',
            margin: '28px 0 24px',
          }}>
            <span style={{ fontSize: '28px' }}>🏌️</span>
            <span style={{ fontSize: '28px' }}>⛳</span>
            <span style={{ fontSize: '28px' }}>🏌️</span>
            <span style={{ fontSize: '28px' }}>⛳</span>
            <span style={{ fontSize: '28px' }}>🏌️</span>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#FFC857', fontSize: '16px', letterSpacing: '0.2em', margin: 0 }}>
              — 3 HOLES • 14 CLUBS —
            </p>
            <p style={{ color: '#666', fontSize: '12px', marginTop: '6px', margin: 0, letterSpacing: '0.12em' }}>
              CREDIT: 99
            </p>
          </div>

          <button
            data-testid={T.teeOff}
            onClick={startGame}
            style={{
              background: 'none', border: 'none', color: '#4AE54A',
              fontSize: '20px', fontFamily: "'Press Start 2P', monospace",
              cursor: 'pointer', marginTop: '20px', letterSpacing: '0.08em',
              opacity: blinking ? 1 : 0.25,
              transition: 'opacity 120ms',
              padding: '6px 10px',
            }}
          >
            ▶ TEE OFF ◀
          </button>

          <div style={{ marginTop: '18px' }}>
            <button
              data-testid={T.leaderboardBtn}
              onClick={() => setGameView('leaderboard')}
              style={{
                background: 'none', border: 'none', color: '#FFC857',
                fontSize: '13px', fontFamily: "'IBM Plex Mono', monospace",
                letterSpacing: '0.22em', cursor: 'pointer',
                textTransform: 'uppercase', padding: '6px 10px',
              }}
            >
              🏆 — LEADERBOARD —
            </button>
          </div>

          <div style={{
            marginTop: '40px', fontSize: '10px', color: '#4C956C',
            textAlign: 'center', lineHeight: 1.9, letterSpacing: '0.12em',
          }}>
            <div>DRAG TO AIM &nbsp;•&nbsp; HOLD SWING, RELEASE TO HIT</div>
            <div>◀ ▶ CLUB &nbsp;•&nbsp; SPACE: POWER</div>
          </div>

          <button
            data-testid={T.exit}
            onClick={() => navigate('/')}
            style={{
              marginTop: '28px', background: 'transparent', border: 'none',
              color: '#555', fontSize: '11px', letterSpacing: '0.2em',
              cursor: 'pointer', textTransform: 'uppercase',
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            ← BACK TO SHOP
          </button>

          <p style={{ color: '#333', fontSize: '9px', marginTop: '28px', letterSpacing: '0.12em' }}>
            © 2025 THE LOOP GOLF
          </p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // SCORECARD SCREEN
  // ===================================================================
  if (gameView === 'scorecard') {
    const total = scores.reduce((a, b) => a + b, 0);
    const diff = total - totalPar;
    return (
      <div
        data-testid={T.scorecard}
        style={{
          position: 'fixed', inset: 0, background: COLORS.bg, color: COLORS.white,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
          fontFamily: "'Oswald', 'IBM Plex Mono', system-ui, sans-serif",
          padding: '30px 20px', overflowY: 'auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: '560px' }}>
          <h2 style={{
            fontSize: 'clamp(22px, 5vw, 32px)', color: COLORS.gold, textAlign: 'center', margin: 0,
            letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 800,
          }}>
            Round Complete
          </h2>
          <div style={{ fontSize: '12px', color: COLORS.gray, letterSpacing: '0.2em', textAlign: 'center', marginTop: '6px', textTransform: 'uppercase' }}>
            The Loop • Amen Corner
          </div>
          <div style={{
            height: '2px', marginTop: '16px', marginBottom: '18px',
            background: 'linear-gradient(90deg, transparent, rgba(192,192,192,0.4), transparent)',
          }} />
          <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '4px', overflow: 'hidden', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', background: 'rgba(0,0,0,0.4)', fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: COLORS.gray }}>
              <div style={{ padding: '10px' }}>Hole</div>
              <div style={{ padding: '10px' }}>Par</div>
              <div style={{ padding: '10px' }}>Score</div>
              <div style={{ padding: '10px' }}>Result</div>
            </div>
            {HOLES.map((h, i) => {
              const s = scores[i] ?? 0;
              const result = getScoreName(s, h.par);
              const color = s <= h.par ? COLORS.good : s > h.par + 1 ? COLORS.bad : COLORS.gold;
              return (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                  fontSize: '14px', borderTop: '1px solid rgba(192,192,192,0.08)',
                  background: i % 2 === 0 ? 'rgba(192,192,192,0.03)' : 'transparent',
                }}>
                  <div style={{ padding: '10px', color: COLORS.silver }}>{h.number}</div>
                  <div style={{ padding: '10px', color: COLORS.silver }}>{h.par}</div>
                  <div style={{ padding: '10px', color: '#fff', fontWeight: 700 }}>{s}</div>
                  <div style={{ padding: '10px', color, fontWeight: 700 }}>{result}</div>
                </div>
              );
            })}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
              fontSize: '14px', fontWeight: 700,
              background: 'rgba(212,175,55,0.1)', borderTop: '2px solid rgba(212,175,55,0.3)',
            }}>
              <div style={{ padding: '10px', color: COLORS.gold }}>TOTAL</div>
              <div style={{ padding: '10px', color: COLORS.silver }}>{totalPar}</div>
              <div style={{ padding: '10px', color: '#fff' }}>{total}</div>
              <div style={{ padding: '10px', color: diff === 0 ? COLORS.good : diff > 0 ? COLORS.bad : COLORS.good }}>
                {diff === 0 ? 'EVEN' : diff > 0 ? `+${diff}` : `${diff}`}
              </div>
            </div>
          </div>
          <div style={{
            padding: '18px 18px 16px', marginBottom: '18px', borderRadius: '6px',
            background: 'rgba(212,175,55,0.06)', border: `1px solid ${COLORS.borderGold}`,
          }}>
            <div style={{
              fontSize: '11px', color: COLORS.gold, letterSpacing: '0.22em',
              textTransform: 'uppercase', fontWeight: 700, textAlign: 'center', marginBottom: '12px',
            }}>
              Submit to Leaderboard
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                data-testid={T.playerNameInput}
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && playerName.trim() && !submittingScore) submitScore(); }}
                placeholder="Enter your name..."
                maxLength={40}
                disabled={submittingScore}
                style={{
                  flex: '1 1 180px', minWidth: '160px',
                  padding: '12px 14px', borderRadius: '4px',
                  background: 'rgba(0,0,0,0.45)', border: `1px solid ${COLORS.border}`,
                  color: '#fff', fontSize: '14px', letterSpacing: '0.08em',
                  fontFamily: 'inherit', outline: 'none',
                }}
              />
              <button
                data-testid={T.submitScore}
                onClick={submitScore}
                disabled={!playerName.trim() || submittingScore}
                style={{
                  padding: '12px 22px',
                  background: !playerName.trim() || submittingScore ? 'rgba(212,175,55,0.45)' : COLORS.gold,
                  color: '#000', border: 'none', borderRadius: '4px',
                  fontSize: '13px', fontWeight: 800, letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  cursor: !playerName.trim() || submittingScore ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', minWidth: '110px',
                }}
              >
                {submittingScore ? 'Saving...' : 'Submit'}
              </button>
            </div>
            {submitError && (
              <div style={{
                marginTop: '10px', fontSize: '11px', color: COLORS.bad,
                letterSpacing: '0.08em', textAlign: 'center',
              }}>
                {submitError}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              data-testid={T.playAgain}
              onClick={startGame}
              style={{
                padding: '14px 28px', background: COLORS.gold, color: '#000',
                border: 'none', fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: '14px',
              }}
            >
              Play Again
            </button>
            <button
              data-testid={T.exitScorecard}
              onClick={() => navigate('/')}
              style={{
                padding: '14px 28px', background: 'transparent',
                border: `1px solid ${COLORS.gray}`, color: COLORS.silver,
                fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px',
              }}
            >
              Exit to Shop
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================================================================
  // LEADERBOARD SCREEN
  // ===================================================================
  if (gameView === 'leaderboard') {
    const total = scores.reduce((a, b) => a + b, 0);
    const diff = total - totalPar;
    const hasRound = scores.length > 0;
    return (
      <div
        data-testid={T.leaderboard}
        style={{
          position: 'fixed', inset: 0, background: COLORS.bg, color: COLORS.white,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
          fontFamily: "'Oswald', 'IBM Plex Mono', system-ui, sans-serif",
          padding: '30px 20px', overflowY: 'auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: '620px' }}>
          <div style={{
            fontSize: '11px', letterSpacing: '0.3em', color: COLORS.gray,
            textAlign: 'center', textTransform: 'uppercase', marginBottom: '6px',
          }}>
            The Loop • Amen Corner
          </div>
          <h2 style={{
            fontSize: 'clamp(28px, 6vw, 42px)', fontWeight: 800, textAlign: 'center', margin: 0,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            <span style={{ color: COLORS.white }}>Leader</span>
            <span style={{ color: COLORS.gold }}>board</span>
          </h2>

          {hasRound && (
            <div style={{
              textAlign: 'center', marginTop: '18px', padding: '14px 18px',
              background: 'rgba(212,175,55,0.08)', border: `1px solid ${COLORS.borderGold}`,
              borderRadius: '6px',
            }}>
              <div style={{ fontSize: '10px', color: COLORS.gray, letterSpacing: '0.22em', textTransform: 'uppercase' }}>
                Your Round
              </div>
              <div style={{ fontSize: '30px', fontWeight: 800, color: '#fff', marginTop: '2px' }}>
                {total}
              </div>
              <div style={{
                fontSize: '13px', fontWeight: 700,
                color: diff <= 0 ? COLORS.good : COLORS.bad,
                letterSpacing: '0.1em',
              }}>
                {diff === 0 ? 'EVEN PAR' : diff > 0 ? `+${diff} OVER` : `${diff} UNDER`}
              </div>
            </div>
          )}

          <div style={{
            height: '2px', margin: '18px 0',
            background: 'linear-gradient(90deg, transparent, rgba(192,192,192,0.4), transparent)',
          }} />

          <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: '4px', overflow: 'hidden', marginBottom: '22px' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '44px 1fr 70px 70px 74px',
              background: 'rgba(0,0,0,0.5)', fontSize: '10px',
              letterSpacing: '0.14em', textTransform: 'uppercase', color: COLORS.gray,
            }}>
              <div style={{ padding: '9px 8px', textAlign: 'center' }}>#</div>
              <div style={{ padding: '9px 8px' }}>Player</div>
              <div style={{ padding: '9px 8px', textAlign: 'center' }}>Score</div>
              <div style={{ padding: '9px 8px', textAlign: 'center' }}>+/-</div>
              <div style={{ padding: '9px 8px', textAlign: 'right' }}>Date</div>
            </div>
            {leaderboard.length === 0 ? (
              <div style={{
                padding: '26px', textAlign: 'center', color: '#707070',
                fontSize: '13px', letterSpacing: '0.14em',
              }}>
                No scores yet. Be the first!
              </div>
            ) : (
              leaderboard.map((entry, i) => {
                const isYou = entry.id === savedScoreId;
                const rel = entry.relative_to_par;
                const medal = i === 0 ? '🏆' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`;
                let date = '';
                try {
                  if (entry.created_at) {
                    const d = new Date(entry.created_at);
                    date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }
                } catch (e) { /* noop */ }
                return (
                  <div
                    key={entry.id || i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '44px 1fr 70px 70px 74px',
                      fontSize: '13px',
                      background: isYou ? 'rgba(212,175,55,0.14)' : i % 2 === 0 ? 'rgba(192,192,192,0.03)' : 'transparent',
                      borderTop: '1px solid rgba(192,192,192,0.08)',
                      borderLeft: isYou ? `3px solid ${COLORS.gold}` : '3px solid transparent',
                    }}
                  >
                    <div style={{
                      padding: '10px 8px', textAlign: 'center',
                      color: i === 0 ? COLORS.gold : i === 1 ? COLORS.silver : i === 2 ? '#cd7f32' : '#707070',
                      fontWeight: 700,
                    }}>
                      {medal}
                    </div>
                    <div style={{ padding: '10px 8px', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      <span style={{
                        color: isYou ? COLORS.gold : COLORS.silver, fontWeight: 700,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {entry.player_name}
                      </span>
                      {isYou && (
                        <span style={{
                          fontSize: '9px', color: COLORS.gold, letterSpacing: '0.18em',
                          border: `1px solid ${COLORS.borderGold}`, padding: '1px 5px',
                          borderRadius: '2px', flexShrink: 0,
                        }}>YOU</span>
                      )}
                    </div>
                    <div style={{ padding: '10px 8px', textAlign: 'center', color: '#fff', fontWeight: 700 }}>
                      {entry.total_strokes}
                    </div>
                    <div style={{
                      padding: '10px 8px', textAlign: 'center', fontWeight: 700,
                      color: rel < 0 ? COLORS.good : rel === 0 ? COLORS.silver : COLORS.bad,
                    }}>
                      {rel === 0 ? 'E' : rel > 0 ? `+${rel}` : `${rel}`}
                    </div>
                    <div style={{ padding: '10px 8px', textAlign: 'right', color: '#707070', fontSize: '11px' }}>
                      {date}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={startGame}
              style={{
                padding: '14px 28px', background: COLORS.gold, color: '#000',
                border: 'none', fontWeight: 700, letterSpacing: '0.12em',
                textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
                fontSize: '14px',
              }}
            >
              Play Again
            </button>
            <button
              onClick={() => { setGameView('title'); fetchLeaderboard(); }}
              style={{
                padding: '14px 28px', background: 'transparent',
                border: `1px solid ${COLORS.gray}`, color: COLORS.silver,
                fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px',
              }}
            >
              Title Screen
            </button>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '14px 28px', background: 'transparent',
                border: `1px solid ${COLORS.gray}`, color: COLORS.silver,
                fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px',
              }}
            >
              Exit to Shop
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===================================================================
  // PLAYING SCREEN
  // ===================================================================
  const hole = HOLES[currentHole];
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: COLORS.bg, color: COLORS.white,
        fontFamily: "'Oswald', 'IBM Plex Mono', system-ui, sans-serif",
        userSelect: 'none', overflow: 'hidden', touchAction: 'none',
      }}
    >
      {/* 3D Canvas mount */}
      <div
        ref={mountRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: gameState === 'aiming' ? 'grab' : 'default' }}
      />

      {/* Top-left: hole label */}
      <div style={{
        position: 'absolute', top: '10px', left: '12px', zIndex: 20, pointerEvents: 'none',
        fontSize: '10px', color: COLORS.gray, letterSpacing: '0.2em', textTransform: 'uppercase',
      }}>
        The Loop • Amen Corner
      </div>

      {/* Top-right: hole info panel */}
      <div style={{
        position: 'absolute', top: 0, right: 0, zIndex: 20, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.82), rgba(0,0,0,0.58))',
        padding: '12px 18px', borderRadius: '0 0 0 8px',
        borderBottom: `2px solid ${COLORS.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '18px', fontSize: '13px' }}>
          <div>
            <span style={{ color: COLORS.gray, letterSpacing: '0.12em', fontSize: '10px' }}>HOLE </span>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: '22px' }}>{hole.number}</span>
          </div>
          <div>
            <span style={{ color: COLORS.gray, letterSpacing: '0.12em', fontSize: '10px' }}>PAR </span>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: '22px' }}>{hole.par}</span>
          </div>
          <div>
            <span style={{ color: COLORS.gray, letterSpacing: '0.12em', fontSize: '10px' }}>DIST </span>
            <span style={{ color: COLORS.gold, fontWeight: 800, fontSize: '22px' }}>{distToHole}y</span>
          </div>
        </div>
        <div style={{ fontSize: '11px', color: COLORS.gold, letterSpacing: '0.14em', marginTop: '2px', textTransform: 'uppercase' }}>
          {hole.name}
        </div>
      </div>

      {/* Center message overlay */}
      {message && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 30, pointerEvents: 'none',
          padding: '18px 36px', borderRadius: '6px',
          background: 'rgba(0,0,0,0.92)', border: `2px solid ${COLORS.borderGold}`,
        }}>
          <div style={{
            fontSize: 'clamp(28px, 6vw, 44px)', color: COLORS.gold,
            letterSpacing: '0.12em', fontWeight: 800, textTransform: 'uppercase', textAlign: 'center',
          }}>
            {message}
          </div>
        </div>
      )}

      {/* Vertical power meter (left side) */}
      {gameState === 'power' && (
        <div style={{
          position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
          zIndex: 20, pointerEvents: 'none',
          width: '30px', height: '200px',
          background: 'rgba(0,0,0,0.85)', border: `2px solid ${COLORS.border}`,
          borderRadius: '4px', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            height: `${power}%`,
            background: power < 50 ? 'linear-gradient(0deg, #22c55e, #4ade80)' :
                       power < 80 ? 'linear-gradient(0deg, #d4af37, #e8cc6e)' :
                                    'linear-gradient(0deg, #ef4444, #f87171)',
          }} />
          <div style={{
            position: 'absolute', top: '4px', left: 0, right: 0, zIndex: 2,
            textAlign: 'center', color: '#fff', fontSize: '10px', fontWeight: 700,
          }}>
            {Math.round(power)}%
          </div>
        </div>
      )}

      {/* Aim nudge buttons (mobile helper) */}
      {gameState === 'aiming' && (
        <>
          <button
            data-testid={T.aimLeft}
            onClick={() => nudgeAim(-1)}
            style={{
              position: 'absolute', left: '14px', bottom: '130px', zIndex: 25,
              width: '52px', height: '52px', borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)', border: `2px solid ${COLORS.border}`,
              color: '#fff', fontSize: '22px', cursor: 'pointer', fontFamily: 'inherit',
            }}
            aria-label="Aim left"
          >
            ◀
          </button>
          <button
            data-testid={T.aimRight}
            onClick={() => nudgeAim(1)}
            style={{
              position: 'absolute', left: '74px', bottom: '130px', zIndex: 25,
              width: '52px', height: '52px', borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)', border: `2px solid ${COLORS.border}`,
              color: '#fff', fontSize: '22px', cursor: 'pointer', fontFamily: 'inherit',
            }}
            aria-label="Aim right"
          >
            ▶
          </button>
        </>
      )}

      {/* SWING button (right side) */}
      {(gameState === 'aiming' || gameState === 'power') && (
        <button
          data-testid={T.swing}
          onPointerDown={handleSwingDown}
          onPointerUp={handleSwingUp}
          onPointerCancel={handleSwingUp}
          onTouchStart={handleSwingDown}
          onTouchEnd={handleSwingUp}
          style={{
            position: 'absolute', right: '18px', bottom: '100px', zIndex: 30,
            minWidth: '130px', padding: '16px 22px',
            background: gameState === 'power' ? '#ef4444' : COLORS.gold,
            color: gameState === 'power' ? '#fff' : '#000',
            border: 'none', borderRadius: '10px',
            fontSize: '14px', fontWeight: 800, letterSpacing: '0.2em',
            textTransform: 'uppercase', cursor: 'pointer',
            touchAction: 'none', fontFamily: 'inherit',
            boxShadow: gameState === 'power'
              ? '0 0 26px rgba(239,68,68,0.55)'
              : '0 0 26px rgba(212,175,55,0.4)',
            transform: gameState === 'power' ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 160ms, background-color 160ms, box-shadow 160ms',
          }}
        >
          {gameState === 'power' ? 'RELEASE!' : '⛳ SWING'}
        </button>
      )}

      {/* Aim hint */}
      {gameState === 'aiming' && (
        <div style={{
          position: 'absolute', bottom: '98px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 20, pointerEvents: 'none',
          padding: '6px 14px', borderRadius: '4px',
          background: 'rgba(0,0,0,0.55)', color: 'rgba(255,255,255,0.7)',
          fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase',
        }}>
          Drag to Aim &nbsp;•&nbsp; Tap Swing
        </div>
      )}

      {/* Holed overlay */}
      {gameState === 'holed' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 40,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', padding: '20px',
        }}>
          <div style={{
            textAlign: 'center', padding: '34px 30px', borderRadius: '6px',
            background: COLORS.panelDark, border: `2px solid ${COLORS.borderGold}`, maxWidth: '420px', width: '100%',
          }}>
            <div style={{ fontSize: 'clamp(28px, 6vw, 44px)', color: COLORS.gold, fontWeight: 800, letterSpacing: '0.08em', marginBottom: '10px' }}>
              {message}
            </div>
            <div style={{ fontSize: '16px', color: COLORS.silver, marginBottom: '4px' }}>
              {strokes} Stroke{strokes !== 1 ? 's' : ''} • Par {hole.par}
            </div>
            <div style={{ fontSize: '12px', color: COLORS.gray, marginBottom: '20px', letterSpacing: '0.08em' }}>
              Hole {hole.number} — {hole.name}
            </div>
            <button
              data-testid={T.nextHole}
              onClick={nextHole}
              style={{
                padding: '12px 28px', background: COLORS.gold, color: '#000',
                border: 'none', fontSize: '14px', fontWeight: 800,
                letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer',
                fontFamily: 'inherit', borderRadius: '4px',
              }}
            >
              {currentHole >= HOLES.length - 1 ? 'View Scorecard' : 'Next Hole ▶'}
            </button>
          </div>
        </div>
      )}

      {/* Bottom HUD bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.9))',
        borderTop: `1px solid ${COLORS.border}`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', pointerEvents: 'auto' }}>
            <button
              data-testid={T.exit}
              onClick={() => navigate('/')}
              style={{
                background: 'transparent', border: 'none', color: COLORS.gray,
                fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit', padding: '4px 6px',
              }}
            >
              Exit
            </button>
            <div style={{ width: '1px', height: '16px', background: '#303030' }} />
            <button
              data-testid={T.clubPrev}
              onClick={() => setClubIdx((i) => Math.max(0, i - 1))}
              disabled={gameState !== 'aiming'}
              style={{
                background: 'transparent', border: 'none',
                color: gameState === 'aiming' ? COLORS.silver : '#505050',
                fontSize: '14px', cursor: gameState === 'aiming' ? 'pointer' : 'not-allowed',
                padding: '4px 6px', fontFamily: 'inherit',
              }}
            >
              ◀
            </button>
            <div style={{ minWidth: '90px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: COLORS.gold, fontWeight: 800, letterSpacing: '0.08em' }}>{club.abbr}</div>
              <div style={{ fontSize: '9px', color: '#909090', letterSpacing: '0.08em', lineHeight: 1.2 }}>{club.name}</div>
            </div>
            <button
              data-testid={T.clubNext}
              onClick={() => setClubIdx((i) => Math.min(CLUBS.length - 1, i + 1))}
              disabled={gameState !== 'aiming'}
              style={{
                background: 'transparent', border: 'none',
                color: gameState === 'aiming' ? COLORS.silver : '#505050',
                fontSize: '14px', cursor: gameState === 'aiming' ? 'pointer' : 'not-allowed',
                padding: '4px 6px', fontFamily: 'inherit',
              }}
            >
              ▶
            </button>
            <div style={{ width: '1px', height: '16px', background: '#303030' }} />
            <div style={{ fontSize: '10px', color: '#808080', letterSpacing: '0.08em' }}>{club.maxDist}y max</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#fff', flexWrap: 'wrap' }}>
            <div><span style={{ color: '#808080' }}>LIE </span><span style={{ fontWeight: 700 }}>{lie}</span></div>
            <div style={{ width: '1px', height: '14px', background: '#303030' }} />
            <div><span style={{ color: '#808080' }}>STRK </span><span style={{ fontWeight: 700 }}>{strokes}</span></div>
            <div style={{ width: '1px', height: '14px', background: '#303030' }} />
            <div><span style={{ color: '#808080' }}>WIND </span><span style={{ color: COLORS.good, fontWeight: 700 }}>{wind.speed}mph</span><span style={{ color: '#808080', marginLeft: '2px' }}>{windDirLabel()}</span></div>
            <div style={{ width: '1px', height: '14px', background: '#303030' }} />
            <div><span style={{ color: '#808080' }}>DIST </span><span style={{ fontWeight: 700 }}>{distToHole}y</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheLoopGame;
